import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { syncFromAnotherSource, getCollabState } from '@atlaskit/prosemirror-collab';
import { tintDirtyTransaction } from '@atlaskit/editor-common/collab';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { Step as ProseMirrorStep } from '@atlaskit/editor-prosemirror/transform';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ACTION } from '@atlaskit/editor-common/analytics';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import { fg } from '@confluence/feature-gating';

import type { StepsDatabase } from '../stepsDatabase';
import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';
import { resetProsemirrorHistoryPlugin } from '../history';
import { fireOfflineStorageAnalyticsEvent } from '../analytics';

import { setInactiveSyncingCommand } from './commands';

const ENABLE_EDITOR_TIMEOUT = 3000;

export type Status =
	| 'granted_initial'
	| 'granted_recovery'
	| 'waiting_initial'
	| 'waiting_recovery'
	| 'init';

export const nextStatus = (status: Status, granted?: boolean) => {
	switch (status) {
		case 'init':
			return granted ? 'granted_initial' : 'waiting_initial';
		case 'waiting_initial':
		case 'waiting_recovery':
			return granted ? 'granted_recovery' : 'waiting_recovery';
		case 'granted_initial':
		case 'granted_recovery':
		default:
			return status;
	}
};

export const hasHistory = (api: PublicPluginAPI<EditorOfflineStoragePlugin> | undefined) => {
	const historyState = api?.history?.sharedState?.currentState?.();
	return (historyState?.done?.eventCount ?? 0) > 0 || (historyState?.undone?.eventCount ?? 0) > 0;
};

type Options = {
	createOfflineVersion: (props: { doc: PMNode; steps: ProseMirrorStep[] }) => void;
};

/**
 * Coordinates the resynchronization of the offline edits between tabs when coming back online.
 *
 * This function acquires a lock on the StepsDatabase to ensure that only one tab is processing the resync at a time.
 *
 * The tab can be one of the following states:
 * - `granted_initial`: The tab has the lock and is ready to process the initial sync.
 * - `waiting_initial`: The tab is waiting for the lock to be granted, and is on standby waiting for the tab with the lock to sync.
 * - `granted_recovery`: The tab has the lock and is ready to process the recovery sync, since the initial sync by the other tab has failed (e.g. tab was closed early).
 * - `waiting_recovery`: The tab is waiting for the lock to be granted, and is on standby waiting for the tab with the lock to sync.
 *
 * @param api The public plugin API
 * @param stepsDatabase The StepsDatabase instance
 * @param editorView The Prosemirror EditorView instance
 * @param isActiveTab The boolean indicating if the current tab is the active tab
 * @param options.createOfflineVersion Callback to create the offline version
 * @returns A promise that resolves immediately
 */
export async function resyncOnline(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	editorView: EditorView,
	isActiveTab: boolean,
	options: Options,
) {
	if (api?.connectivity?.sharedState.currentState()?.mode === 'offline') {
		return;
	}

	let status: Status = 'init';
	let previousStatus: Status = 'init';

	const lockCallback = async (granted?: boolean) => {
		previousStatus = status;
		status = nextStatus(status, granted);

		// If we're transitioning to the same state, we might be in a loop.
		// This can happen if repeated timeouts occur (e.g., waiting_recovery -> waiting_recovery).
		if (fg('platform_editor_offline_editing_web_ga_fixes')) {
			if (status === previousStatus) {
				// Fallback: enable the editor and give up on syncing
				api?.core?.actions?.execute(setInactiveSyncingCommand(false));
				return;
			}
		}

		const { steps, docJSON, version } = await stepsDatabase.getData(editorView?.state?.schema);

		const sendResyncAnalytics = () => {
			fireOfflineStorageAnalyticsEvent({
				api,
				action: ACTION.OFFLINE_STORAGE_RESYNC_ONLINE,
				resyncStatus: status,
				isActiveTab,
			});
		};

		const unconfirmedSteps = getCollabState(editorView.state)?.unconfirmed ?? [];

		switch (status) {
			case 'granted_initial':
				if (steps.length > 0) {
					sendResyncAnalytics();

					// Clear offline lock on unconfirmed steps
					unconfirmedSteps.forEach((s) => {
						if (s.origin instanceof Transaction) {
							s.origin.setMeta('isOffline', false);
							s.origin.setMeta('wasOffline', true);
						}
					});
					if (fg('platform_editor_offline_version_sync')) {
						// Create the offline version based on a snapshot of the doc at the time of returning online
						options.createOfflineVersion?.({
							doc: editorView.state.doc,
							steps: unconfirmedSteps.map((s) => s.step),
						});
					}
					setTimeout(() => {
						api?.core?.actions?.execute(setInactiveSyncingCommand(false));
					}, ENABLE_EDITOR_TIMEOUT);
				} else if (docJSON && version !== null && unconfirmedSteps.length !== 0) {
					// So we're here because another tab has already synced but the steps in the DB have been deleted
					// In this case we revert to the document before going offline and allow collab to resync us
					sendResyncAnalytics();
					if (hasHistory(api)) {
						resetProsemirrorHistoryPlugin(editorView);
					}

					const tr = syncFromAnotherSource(editorView.state, version, docJSON, []);
					setInactiveSyncingCommand(true)({ tr });
					fg('platform_editor_offline_editing_web_ga_fixes') && tintDirtyTransaction(tr);
					editorView.dispatch(tr);

					void stepsDatabase.acquireSyncLock(lockCallback, true, 'resync');
				} else {
					// At this point we've synced and can enable the editor
					setTimeout(() => {
						api?.core?.actions?.execute(setInactiveSyncingCommand(false));
					}, ENABLE_EDITOR_TIMEOUT);
				}
				break;

			case 'waiting_initial':
				// Reset to base document so we don't duplicate the unconfirmed steps, when we come back online we'll sync with the latest changes
				if (docJSON && version !== null && unconfirmedSteps?.length !== 0) {
					sendResyncAnalytics();

					if (hasHistory(api)) {
						resetProsemirrorHistoryPlugin(editorView);
					}

					const tr = syncFromAnotherSource(editorView.state, version, docJSON, []);
					setInactiveSyncingCommand(true)({ tr });
					editorView.dispatch(tr);

					void stepsDatabase.acquireSyncLock(lockCallback, true, 'resync');
				}
				break;

			case 'granted_recovery':
				if (steps.length > 0 && docJSON && version !== null) {
					sendResyncAnalytics();

					if (hasHistory(api)) {
						resetProsemirrorHistoryPlugin(editorView);
					}
					editorView.dispatch(syncFromAnotherSource(editorView.state, version, docJSON, steps));
				}
				setTimeout(() => {
					api?.core?.actions?.execute(setInactiveSyncingCommand(false));
				}, ENABLE_EDITOR_TIMEOUT);
				break;

			case 'waiting_recovery':
				if (steps.length > 0 && docJSON && version !== null) {
					sendResyncAnalytics();
				}
				api?.core?.actions?.execute(setInactiveSyncingCommand(false));
				void stepsDatabase.acquireSyncLock(lockCallback, true, 'resync');
				break;

			default:
				break;
		}
	};

	void stepsDatabase.acquireSyncLock(lockCallback, false, 'resync');
}
